import { fetchOrganizations } from 'admin/actions/organization';
import { getPrivateMoneys } from 'admin/actions/private-moneys';
import { createShopApplication } from 'admin/actions/shop-applications';
import { createShop } from 'admin/actions/shops';
import { getActiveUserTagGroupsWithItems } from 'admin/actions/user-tag-group';
import { attachUserTag } from 'admin/actions/user-tags';
import { organizationManagerAllowed, serviceManagerAllowed } from 'admin/authorities';
import Select from 'admin/components/atoms/Select';
import TagSelectionEditor from 'admin/components/organisms/TagSelectionEditor';
import ToggleSwitch from 'admin/components/atoms/ToggleSwitch';
import Breadcrumb from 'admin/components/shared/Breadcrumb';
import Button from 'admin/components/shared/Button';
import Input from 'admin/components/shared/Input';
import { PRIVATE_MONEY_TYPE } from 'admin/constants/privateMoney';
import { USAGE_TYPE_OPTIONS } from 'admin/constants/usageType';
import { EXCLUDED_ISSUER_ORG_CODE } from 'admin/constants/userTagGroupLabels';
import _ from 'lodash';
import React, { Component } from 'react';
import CSSModules from 'react-css-modules';
import { connect } from 'react-redux';
import { Alert } from 'reactstrap';
import styled from 'styled-components';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { testEmailAllowedLetters } from '~/javascripts/admin/utils/email';
import {
  hasParameterError,
  pickIssuerOrgCodeFromMoneys,
  validateShopForm,
} from '~/javascripts/admin/utils/shopFormValidation';
import Required from '../../atoms/Required';
import styles from './styles.scss';

const LabeledError = styled.div`
  color: red;
`;

// 店舗ラベルは他の店舗情報とシステム特性が異なるため、視覚的に独立したセクションとして表示する。
const LabelSection = styled.div`
  margin: 16px 0;
  padding: 16px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #fafafa;
`;

const LabelSectionTitle = styled.div`
  font-weight: bold;
  color: #555;
  margin-bottom: 12px;
`;


class CreateShop extends Component {
  constructor(props) {
    super(props);
    this.state = {
      createdShop: {},
      errorResponse: {},
      shopForm: {
        shop_name: '',
        shop_name_furigana: '',
        shop_tel: '',
        shop_email: '',
        shop_postal_code: '',
        shop_address: '',
        shop_external_id: '',
        contact_person_name: '',
        contact_person_name_furigana: '',
        organization_code: props.user.organization?.code ?? '',
        private_money_ids: [],
        can_topup_all: false,
        usage_type: '',
      },
      // 発行体ユーザ向けに加盟組織を含む全組織を選択肢として保持。
      // 加盟組織ユーザは fetch せず自組織のみ表示 (componentDidMount で fetch スキップ)。
      organizationOptions: props.user.organization
        ? [{ label: props.user.organization.name, value: props.user.organization.code }]
        : [],
      // fetchOrganizations 失敗時に true (発行体ユーザにのみ表示)
      organizationFetchFailed: false,
      // 選択中の組織が加盟しているマネー一覧 (organization_code をキーに getPrivateMoneys で取得)
      availableMoneys: [],
      // race condition 対策: どの組織コードに対する fetch かを追跡
      availableMoneysOrgCode: null,
      // getPrivateMoneys 失敗時に true
      availableMoneysFetchFailed: false,
      // 発行体orgの有効タググループ（正規化済み）と、付与する行（add-group 方式）
      availableTagGroups: [],
      // [{ groupId, itemIds: [] }] 形式。「グループを選択 → ラベルを選択」を行単位で管理。
      tagSelections: [],
      tagGroupsIssuerOrgCode: null,
      tagGroupsLoading: false,
      tagGroupsFetchFailed: false,
      tagAttachWarning: false,
      isModalOpen: false,
      selectedMoneyOptions: [],
      showMoneySelectionError: false,
      showShopNameError: false,
      emailError: false,
      // 必須項目の空欄エラー (key: shopForm のキー, value: true)
      emptyFieldErrors: {},
      // フォーマットエラー (key: shopForm のキー, value: エラーメッセージ文字列)
      formatFieldErrors: {},
    };
  }

  componentDidMount() {
    const { selectedPrivateMoney, user } = this.props;
    // 初期表示の組織に紐づくマネー一覧を取得
    const initialOrgCode = this.state.shopForm.organization_code;
    if (initialOrgCode) {
      this.fetchAvailableMoneys(initialOrgCode);
    }
    if (!selectedPrivateMoney) return;
    if (!user.isIssuerOfMoney(selectedPrivateMoney)) return; // 加盟組織ユーザは自組織固定
    const query = `private_money_id=${selectedPrivateMoney.id}&per_page=10000`;
    fetchOrganizations(query)
      .then((res) => {
        const issuer = selectedPrivateMoney.organization;
        const options = [
          ...(issuer ? [{ label: `${issuer.name}(${issuer.code})`, value: issuer.code }] : []),
          ...res.rows.map((o) => ({ label: `${o.name}(${o.code})`, value: o.code })),
        ];
        this.setState({ organizationOptions: options, organizationFetchFailed: false });
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch organizations', err);
        }
        // 取得失敗時は自組織のみで継続しつつ、UI 上で取得失敗を可視化
        this.setState({ organizationFetchFailed: true });
      });
  }

  onClick(e) {
    e.preventDefault();

    const isMemberOrgUser = this.isMemberOrgUser();
    const validation = validateShopForm(this.state.shopForm, {
      isMemberOrgUser,
      isMoneySelected: this.isMoneySelected(),
    });

    this.setState({
      showMoneySelectionError: validation.showMoneySelectionError,
      showShopNameError: validation.showShopNameError,
      emailError: validation.emailError,
      emptyFieldErrors: validation.emptyFieldErrors,
      formatFieldErrors: validation.formatFieldErrors,
    });

    if (validation.hasError) {
      return;
    }

    const userTagGroupItemIds = this.state.tagSelections
      .flatMap((row) => row.itemIds)
      .filter(Boolean);

    // 加盟組織ユーザは発行体審査を伴う申請フローを通る。
    // POST /api/shop-applications (kind=new) で店舗登録申込みを送信。
    if (isMemberOrgUser) {
      const { shopForm } = this.state;
      const applicationPayload = {
        organization_code: shopForm.organization_code,
        shop_name: shopForm.shop_name,
        shop_name_furigana: shopForm.shop_name_furigana,
        postal_code: shopForm.shop_postal_code,
        address: shopForm.shop_address,
        tel: shopForm.shop_tel,
        email: shopForm.shop_email,
        contact_person_name: shopForm.contact_person_name,
        contact_person_name_furigana: shopForm.contact_person_name_furigana,
        private_money_ids: shopForm.private_money_ids,
        usage_type: shopForm.usage_type,
        ...(userTagGroupItemIds.length > 0 && { user_tag_group_item_ids: userTagGroupItemIds }),
      };
      createShopApplication(applicationPayload).then((json) =>
        json.id
          ? this.setState({ createdShop: json, errorResponse: {} })
          : this.setState({ createdShop: {}, errorResponse: json })
      );
      return;
    }

    // 発行体直接モード: shop 作成成功後、選択された tag item を attach → 店舗詳細へ遷移
    createShop(this.state.shopForm).then((json) => {
      if (!json.name) {
        this.setState({ createdShop: {}, errorResponse: json });
        return;
      }
      this.setState({ createdShop: json, errorResponse: {}, tagAttachWarning: false });
      const goToDetail = () => this.props.history.push(`/shops/${json.id}`);
      if (userTagGroupItemIds.length === 0) {
        goToDetail();
        return;
      }
      Promise.all(userTagGroupItemIds.map((itemId) => attachUserTag(json.id, itemId)))
        .then(goToDetail)
        .catch((err) => {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.error('Failed to attach user tags', err);
          }
          // タグ attach 失敗時は遷移せず登録画面に留まり warning Alert を表示
          this.setState({ tagAttachWarning: true });
        });
    });
  }

  isMemberOrgUser() {
    const { selectedPrivateMoney, user } = this.props;
    return !!selectedPrivateMoney && !user.isIssuerOfMoney(selectedPrivateMoney);
  }

  isEmptyField(key) {
    return Boolean(this.state.emptyFieldErrors[key]);
  }

  formatErrorOf(key) {
    return this.state.formatFieldErrors[key];
  }

  onSelectMoney = (options) => {
    this.setState({ selectedMoneyOptions: options });
    const moneyIds = options.map((op) => op.value.id);
    this.setShopForm('private_money_ids', moneyIds);
    const selectedMoneys = options.map((op) => op.value);
    const issuerOrgCode = pickIssuerOrgCodeFromMoneys(selectedMoneys);
    this.loadTagGroupsIfNeeded(issuerOrgCode);
  };

  loadTagGroupsIfNeeded(issuerOrgCode) {
    if (issuerOrgCode === this.state.tagGroupsIssuerOrgCode) return;
    if (!issuerOrgCode) {
      this.setState({
        tagGroupsIssuerOrgCode: null,
        availableTagGroups: [],
        tagSelections: [],
        tagGroupsFetchFailed: false,
      });
      return;
    }
    this.setState({ tagGroupsIssuerOrgCode: issuerOrgCode, tagGroupsLoading: true });
    const requestedOrgCode = issuerOrgCode;
    const isStale = () => this.state.tagGroupsIssuerOrgCode !== requestedOrgCode;
    getActiveUserTagGroupsWithItems(issuerOrgCode)
      .then((groups) => {
        if (isStale()) return; // 別の組織のレスポンスが既に反映されている
        this.setState({
          availableTagGroups: groups,
          tagSelections: [],
          tagGroupsLoading: false,
          tagGroupsFetchFailed: false,
        });
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch user tag groups', err);
        }
        if (isStale()) return;
        // 再試行可能にするため tagGroupsIssuerOrgCode を null に戻す
        this.setState({
          tagGroupsLoading: false,
          tagGroupsFetchFailed: true,
          tagGroupsIssuerOrgCode: null,
        });
      });
  }

  setTagSelections = (next) => {
    this.setState({ tagSelections: next });
  };

  fetchAvailableMoneys(orgCode) {
    // 前組織のマネーがセレクタに残り、再 fetch 中に誤選択されるのを防ぐためクリア
    this.setState({
      availableMoneys: [],
      availableMoneysOrgCode: orgCode,
      availableMoneysFetchFailed: false,
    });
    const requestedOrgCode = orgCode;
    const isStale = () => this.state.availableMoneysOrgCode !== requestedOrgCode;
    getPrivateMoneys(`organization_code=${orgCode}&per_page=10000`)
      .then((res) => {
        if (isStale()) return;
        this.setState({ availableMoneys: res.rows.filter((money) => money.organization?.code !== EXCLUDED_ISSUER_ORG_CODE ) });
      })
      .catch((err) => {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Failed to fetch private moneys', err);
        }
        if (isStale()) return;
        this.setState({ availableMoneysFetchFailed: true });
      });
  }

  onSelectOrganization = (opt) => {
    const orgCode = opt ? opt.value : '';
    this.setShopForm('organization_code', orgCode);
    // 組織変更時は加盟マネー候補が変わるため選択中マネーをリセット
    this.onSelectMoney([]);
    if (orgCode) {
      this.fetchAvailableMoneys(orgCode);
    } else {
      this.setState({ availableMoneys: [], availableMoneysOrgCode: null });
    }
  };

  isInvalidParameter(param) {
    return hasParameterError(this.state.errorResponse, 'invalid', param);
  }

  isMissingParameter(param) {
    return hasParameterError(this.state.errorResponse, 'missing', param);
  }

  isShopNameConflict() {
    return (
      !_.isEmpty(this.state.errorResponse) && this.state.errorResponse.type === 'shop_name_conflict'
    );
  }

  isPrivateMoneyUnavailable() {
    return (
      !_.isEmpty(this.state.errorResponse) &&
      this.state.errorResponse.type === 'unavailable_private_money'
    );
  }

  isOrganizationNotFound() {
    return (
      !_.isEmpty(this.state.errorResponse) &&
      this.state.errorResponse.type === 'organization_not_found'
    );
  }

  isUnpermittedAdminUser() {
    return (
      !_.isEmpty(this.state.errorResponse) &&
      this.state.errorResponse.type === 'unpermitted_admin_user'
    );
  }

  isMoneySelected() {
    return (
      !_.isEmpty(this.state.selectedMoneyOptions) && this.state.selectedMoneyOptions.length > 0
    );
  }

  isSuccess() {
    if (_.isEmpty(this.state.createdShop)) return false;
    // 発行体モード: Shop レスポンス (.name)、加盟組織モード: ShopApplication レスポンス (.shop_name)
    const responseShopName =
      this.state.createdShop.name || this.state.createdShop.shop_name;
    return responseShopName === this.state.shopForm.shop_name;
  }

  setShopForm(property, value) {
    const { shopForm } = this.state;
    shopForm[property] = value;
    this.setState({ shopForm });
  }

  toMoneyOptions = (moneys) => {
    return moneys.map((m) => ({ label: m.name, value: m }));
  };

  isAllIssuedMoney = (moneys) => moneys.every((money) => this.props.user.isIssuerOfMoney(money));

  renderTopupToggleButton = () => {
    if (
      serviceManagerAllowed.indexOf(this.props.user.role) !== -1 ||
      (organizationManagerAllowed.indexOf(this.props.user.role) !== -1 &&
        this.isAllIssuedMoney(this.props.user.moneys()))
    ) {
      return (
        <ToggleSwitch
          checked={this.state.shopForm.can_topup_all}
          onChange={(checked) => {
            this.setShopForm('can_topup_all', checked);
          }}
        />
      );
    }
    return '無効';
  };

  render() {
    const { selectedPrivateMoney } = this.props;
    // 加盟組織ユーザ (= 選択中マネーの発行体ではない) は登録ではなく "追加申請" を出す
    const isMemberOrgUser = this.isMemberOrgUser();
    // 自家型発行体は登録先が発行体組織のみのため組織名を readonly に
    const isOwnIssuer = selectedPrivateMoney?.type === PRIVATE_MONEY_TYPE.OWN;
    const pageTitle = isMemberOrgUser ? '店舗追加申請' : '店舗登録';
    const submitButtonLabel = isMemberOrgUser ? '追加の審査申請' : '登録';

    const crumb = [
      { value: 'ホーム', href: '/', active: false },
      { value: '店舗管理', href: '', active: true },
      { value: pageTitle, href: '', active: true },
    ];

    const { showMoneySelectionError, showShopNameError, emailError, organizationOptions } =
      this.state;

    const selectedOrganizationOption =
      organizationOptions.find((o) => o.value === this.state.shopForm.organization_code) || null;
    const isMoneyEmpty = (this.state.selectedMoneyOptions || []).length === 0;
    const tagsLoading = this.state.tagGroupsLoading;

    return (
      <div>
        <Breadcrumb items={crumb} />
        <div styleName="headline">{pageTitle}</div>
        <div styleName="section-title">基本情報</div>
        <form onSubmit={this.onClick.bind(this)}>
          <div styleName="row">
            <div styleName="label">
              組織名
              <Required />
            </div>
            <div styleName="form-item">
              <Select
                options={organizationOptions}
                value={selectedOrganizationOption}
                onChange={this.onSelectOrganization}
                isDisabled={isMemberOrgUser || isOwnIssuer}
                placeholder="組織を選択してください"
                fullWidth
              />
              <Alert color="danger" isOpen={this.isOrganizationNotFound()}>
                指定された組織が見つかりません
              </Alert>
              <Alert color="danger" isOpen={this.isUnpermittedAdminUser()}>
                指定された組織への店舗作成権限がありません
              </Alert>
              <Alert color="warning" isOpen={this.state.organizationFetchFailed && !isMemberOrgUser}>
                組織一覧の取得に失敗しました。再読み込みしてください。現在は自組織のみ選択可能です。
              </Alert>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">
              加盟するマネー
              <Required />
            </div>
            <div styleName="form-item">
              <Select
                options={this.toMoneyOptions(this.state.availableMoneys)}
                value={this.state.selectedMoneyOptions}
                onChange={this.onSelectMoney}
                isMulti
                placeholder="加盟するマネーを選択して下さい"
                fullWidth
              />
              {showMoneySelectionError && (
                <LabeledError>加盟するマネーを選択してください</LabeledError>
              )}
              {this.isMissingParameter('private_money_ids') && (
                <LabeledError>加盟するマネーを選択してください</LabeledError>
              )}
              {this.isPrivateMoneyUnavailable() && (
                <LabeledError>無効なマネーが選択されています</LabeledError>
              )}
              {this.state.availableMoneysFetchFailed && (
                <LabeledError>マネー一覧の取得に失敗しました。再読み込みしてください。</LabeledError>
              )}
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">
              店舗名
              <Required />
            </div>
            <div styleName="form-item">
              <Input onChange={(e) => this.setShopForm('shop_name', e.target.value)} />
              {showShopNameError && <LabeledError>店舗名を入力してください</LabeledError>}
              <Alert color="danger" isOpen={this.isInvalidParameter('shop_name')}>
                店舗名は必須で、256文字以下の文字列である必要があります
              </Alert>
              <Alert color="danger" isOpen={this.isShopNameConflict()}>
                この店舗名は既に登録されています
              </Alert>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">
              店舗名（フリガナ）
              {isMemberOrgUser && <Required />}
            </div>
            <div styleName="form-item">
              <Input
                value={this.state.shopForm.shop_name_furigana}
                onChange={(e) => this.setShopForm('shop_name_furigana', e.target.value)}
              />
              {this.isEmptyField('shop_name_furigana') && (
                <LabeledError>店舗名（フリガナ）を入力してください</LabeledError>
              )}
              {this.formatErrorOf('shop_name_furigana') && (
                <LabeledError>{this.formatErrorOf('shop_name_furigana')}</LabeledError>
              )}
              <Alert color="danger" isOpen={this.isInvalidParameter('shop_name_furigana')}>
                店舗名（フリガナ）は256文字以下の文字列である必要があります
              </Alert>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">
              店舗形態
              <Required />
            </div>
            <div styleName="form-item">
              <Select
                options={USAGE_TYPE_OPTIONS}
                value={
                  USAGE_TYPE_OPTIONS.find((o) => o.value === this.state.shopForm.usage_type) || null
                }
                onChange={(opt) => this.setShopForm('usage_type', opt ? opt.value : '')}
                placeholder="店舗形態を選択してください"
                fullWidth
              />
              {this.isEmptyField('usage_type') && (
                <LabeledError>店舗形態を選択してください</LabeledError>
              )}
              <Alert color="danger" isOpen={this.isInvalidParameter('usage_type')}>
                不正な店舗形態です
              </Alert>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">
              郵便番号
              {isMemberOrgUser && <Required />}
            </div>
            <div styleName="form-item">
              <Input
                placeholder="例）0001111"
                validations={[]}
                onChange={(e) => this.setShopForm('shop_postal_code', e.target.value)}
              />
              {this.isEmptyField('shop_postal_code') && (
                <LabeledError>郵便番号を入力してください</LabeledError>
              )}
              {this.formatErrorOf('shop_postal_code') && (
                <LabeledError>{this.formatErrorOf('shop_postal_code')}</LabeledError>
              )}
              <Alert color="danger" isOpen={this.isInvalidParameter('shop_postal_code')}>
                不正な郵便番号です
              </Alert>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">
              住所
              {isMemberOrgUser && <Required />}
            </div>
            <div styleName="form-item">
              <Input onChange={(e) => this.setShopForm('shop_address', e.target.value)} />
              {this.isEmptyField('shop_address') && (
                <LabeledError>住所を入力してください</LabeledError>
              )}
              <Alert color="danger" isOpen={this.isInvalidParameter('shop_address')}>
                不正な住所の文字列です。住所は256文字以下の文字列である必要があります
              </Alert>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">
              電話番号
              {isMemberOrgUser && <Required />}
            </div>
            <div styleName="form-item">
              <Input
                placeholder="例）08012345678"
                type="tel"
                onChange={(e) => this.setShopForm('shop_tel', e.target.value)}
              />
              {this.isEmptyField('shop_tel') && (
                <LabeledError>電話番号を入力してください</LabeledError>
              )}
              {this.formatErrorOf('shop_tel') && (
                <LabeledError>{this.formatErrorOf('shop_tel')}</LabeledError>
              )}
              <Alert color="danger" isOpen={this.isInvalidParameter('shop_tel')}>
                不正な電話番号です
              </Alert>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">
              メールアドレス
              {isMemberOrgUser && <Required />}
            </div>
            <div styleName="form-item">
              <Input
                placeholder="例）pokepay@example.com"
                value={this.state.shopForm.shop_email}
                onChange={(e) =>
                  testEmailAllowedLetters(e.target.value) &&
                  this.setShopForm('shop_email', e.target.value)
                }
              />
              {this.isEmptyField('shop_email') && (
                <LabeledError>メールアドレスを入力してください</LabeledError>
              )}
              {emailError && <LabeledError>{emailError}</LabeledError>}
              <Alert color="danger" isOpen={this.isInvalidParameter('shop_email')}>
                不正なメールアドレスです
              </Alert>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">
              担当者名
              {isMemberOrgUser && <Required />}
            </div>
            <div styleName="form-item">
              <Input
                value={this.state.shopForm.contact_person_name}
                onChange={(e) => this.setShopForm('contact_person_name', e.target.value)}
              />
              {this.isEmptyField('contact_person_name') && (
                <LabeledError>担当者名を入力してください</LabeledError>
              )}
              <Alert color="danger" isOpen={this.isInvalidParameter('contact_person_name')}>
                担当者名は256文字以下の文字列である必要があります
              </Alert>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">
              担当者名（フリガナ）
              {isMemberOrgUser && <Required />}
            </div>
            <div styleName="form-item">
              <Input
                value={this.state.shopForm.contact_person_name_furigana}
                onChange={(e) => this.setShopForm('contact_person_name_furigana', e.target.value)}
              />
              {this.isEmptyField('contact_person_name_furigana') && (
                <LabeledError>担当者名（フリガナ）を入力してください</LabeledError>
              )}
              {this.formatErrorOf('contact_person_name_furigana') && (
                <LabeledError>{this.formatErrorOf('contact_person_name_furigana')}</LabeledError>
              )}
              <Alert color="danger" isOpen={this.isInvalidParameter('contact_person_name_furigana')}>
                担当者名（フリガナ）は256文字以下の文字列である必要があります
              </Alert>
            </div>
          </div>
          <LabelSection>
            <LabelSectionTitle>店舗ラベル</LabelSectionTitle>
            {isMoneyEmpty ? (
              <div>先にマネーを選択してください</div>
            ) : tagsLoading ? (
              <div>読み込み中...</div>
            ) : (
              <TagSelectionEditor
                groups={this.state.availableTagGroups}
                value={this.state.tagSelections}
                onChange={this.setTagSelections}
              />
            )}
            <Alert
              color="warning"
              isOpen={this.state.tagGroupsFetchFailed}
              style={{ marginTop: 8 }}
            >
              店舗ラベルの取得に失敗しました。ページを再読み込みするか、加盟するマネーを選択し直してください。
            </Alert>
          </LabelSection>
          <div styleName="row">
            <div styleName="label">外部ID</div>
            <div styleName="form-item">
              <Input
                validations={[]}
                onChange={(e) => this.setShopForm('shop_external_id', e.target.value)}
              />
              <Alert color="danger" isOpen={this.isInvalidParameter('shop_external_id')}>
                外部IDは36文字以内の半角英数字記号である必要があります
              </Alert>
            </div>
          </div>
          <div styleName="row">
            <div styleName="label">店舗によるチャージ</div>
            <div styleName="form-item">{this.renderTopupToggleButton()}</div>
          </div>
          <div styleName="button-group">
            <Button type="submit">{submitButtonLabel}</Button>
          </div>
          <Alert color="success" isOpen={this.isSuccess()}>
            {isMemberOrgUser
              ? '店舗の追加申請を完了しました'
              : '店舗の追加を完了しました'}
          </Alert>
          <Alert color="warning" isOpen={this.state.tagAttachWarning}>
            店舗は作成されましたが、商店街エリア / 業種:大分類 タグの設定に失敗しました。店舗編集画面で再設定してください。
          </Alert>
        </form>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  user: state.user,
  selectedPrivateMoney: selectUserPrivateMoney(state),
});

export default connect(mapStateToProps, null)(CSSModules(CreateShop, styles));
